import clsx from "clsx";
import * as React from "react";
import { Text } from "../text/Text.js";
interface TimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  indicatorPosition?: "start" | "end";
  decorator?: "dot" | "icon";
}

const TimelineContext = React.createContext<{
  position: TimelineProps["indicatorPosition"];
  decorator: TimelineProps["decorator"];
}>({
  position: "start",
  decorator: "dot",
});

const useTimelineContext = () => {
  const context = React.useContext(TimelineContext);
  if (context === undefined) {
    throw new Error(
      "useTimelineContext must be used within a TimelineProvider",
    );
  }
  return context;
};

type TimelineStepColorProps =
  | "blue"
  | "red"
  | "purple"
  | "indigo"
  | "brown"
  | "yellow"
  | "light-blue";

interface TimelineItemProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string;
  caption?: string;
  variant?: "default" | "info" | "warning" | "error" | "success";
  border?: "solid" | "dashed";
  decorator?: "dot" | "icon";
}

interface TimelineStepProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string;
  caption?: string;
  icon?: React.ReactNode;
  color?: TimelineStepColorProps;
  border?: "solid" | "dashed";
}

interface TimelineComponent
  extends React.ForwardRefExoticComponent<
    TimelineProps & React.RefAttributes<HTMLDivElement>
  > {
  Item: typeof TimelineItem;
  Step: typeof TimelineStep;
}

const Timeline = React.forwardRef<HTMLDivElement, TimelineProps>(
  (
    { className, indicatorPosition = "start", decorator = "dot", ...props },
    ref,
  ) => {
    return (
      <TimelineContext.Provider
        value={{ position: indicatorPosition, decorator }}
      >
        <div
          ref={ref}
          data-position={indicatorPosition}
          data-decorator={decorator}
          className={clsx("ox-reset ox-TimelineRoot", className)}
          {...props}
        />
      </TimelineContext.Provider>
    );
  },
) as TimelineComponent;

const TimelineItem = React.forwardRef<HTMLDivElement, TimelineItemProps>(
  (props, ref) => {
    const {
      title,
      description,
      decorator,
      caption,
      className,
      variant = "default",
      border = "solid",
      ...rest
    } = props;

    const { position, decorator: contextDecorator } = useTimelineContext();
    const finalDecorator = decorator ?? contextDecorator;

    return (
      <div
        ref={ref}
        data-variant={variant}
        data-border={border}
        data-decorator={finalDecorator}
        data-position={position}
        className={clsx("ox-reset ox-TimelineItem", className)}
        {...rest}
      >
        <Text size="lg" data-part="title" fontWeight="semibold">
          {title}
        </Text>
        {description ? (
          <Text size="md" data-part="description" fontWeight="regular">
            {description}
          </Text>
        ) : null}
        {caption ? (
          <Text size="sm" data-part="caption" fontWeight="regular">
            {caption}
          </Text>
        ) : null}
      </div>
    );
  },
);

const TimelineStep = React.forwardRef<HTMLDivElement, TimelineStepProps>(
  (props, ref) => {
    const {
      title,
      description,
      icon,
      caption,
      className,
      color = "blue",
      border = "solid",
      ...rest
    } = props;

    const { position } = useTimelineContext();
    return (
      <div
        ref={ref}
        data-position={position}
        data-border={border}
        className={clsx("ox-reset ox-TimelineStepItem", className)}
        {...rest}
      >
        <div className="ox-reset ox-TimelineStepIcon" data-color={color}>
          {icon}
        </div>
        <Text size="lg" data-part="title" fontWeight="semibold">
          {title}
        </Text>
        {description ? (
          <Text size="md" data-part="description" fontWeight="regular">
            {description}
          </Text>
        ) : null}
        {caption ? (
          <Text size="sm" data-part="caption" fontWeight="regular">
            {caption}
          </Text>
        ) : null}
      </div>
    );
  },
);

Timeline.Item = TimelineItem;
Timeline.Step = TimelineStep;

TimelineItem.displayName = "Timeline.Item";
Timeline.displayName = "Timeline";
TimelineStep.displayName = "Timeline.Step";

export { Timeline, TimelineStep };
export type { TimelineProps };
